/**
 * Ozel sayfa servisleri (dashboard, sales, tickets, users, stocks...) icin
 * basit REST yardimcisi. dataProvider disinda kalan cagrilarda kullanilir.
 */
import { getPanelAccessToken } from './apiAuthProvider';

export const useApiData = process.env.REACT_APP_USE_API_DATA === 'true';

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  return base.replace(/\/$/, '');
};

const parseJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const request = async (path, options = {}) => {
  const token = getPanelAccessToken();
  const response = await fetch(`${getApiUrl()}/${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const data = await parseJson(response);
  if (!response.ok) {
    const message =
      (data && (Array.isArray(data.message) ? data.message.join(', ') : data.message)) ||
      `API error (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return data;
};

export const apiGet = async (path) => request(path);

/**
 * Paginated list endpoints (sales, tickets, …) — API caps perPage at 1000.
 * Pulls every page when a filter may return more than one page.
 * @param {string} resource path without query, e.g. "sales" or "tickets"
 * @param {URLSearchParams|Record<string,string>} queryParams filters + sort
 * @param {{ perPage?: number }} [options]
 * @returns {Promise<Array>}
 */
export const apiGetAllPages = async (resource, queryParams = {}, options = {}) => {
  const perPage = Math.min(Math.max(Number(options.perPage) || 1000, 1), 1000);
  const base =
    queryParams instanceof URLSearchParams
      ? new URLSearchParams(queryParams)
      : new URLSearchParams(queryParams);
  base.set('perPage', String(perPage));

  const all = [];
  let page = 1;
  let total = Infinity;

  while (all.length < total) {
    const qs = new URLSearchParams(base);
    qs.set('page', String(page));
    const res = await apiGet(`${resource}?${qs.toString()}`);
    const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    total = typeof res?.total === 'number' ? res.total : all.length + rows.length;
    all.push(...rows);
    if (!rows.length || rows.length < perPage || all.length >= total) break;
    page += 1;
  }

  return all;
};

export const apiPost = async (path, body) =>
  request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const apiDelete = async (path) => request(path, { method: 'DELETE' });

export const apiPut = async (path, body) =>
  request(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

/** Multipart file upload → { src, title } (relative /uploads/...) */
export const apiUploadFile = async (file, { folder } = {}) => {
  const form = new FormData();
  form.append('file', file, file.name || 'document');
  const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
  return request(`upload${qs}`, {
    method: 'POST',
    body: form,
  });
};
